import { useEffect, useState } from 'react';
import { useUI } from '../store/ui';
import { useT } from '../lib/i18n';
import { pickNextSplashQuote } from '../lib/splashQuotes';

// Post-login splash. Mounts when the auth event flips to SIGNED_IN (and the
// user is past onboarding, so first-time signups don't see it on the way to
// /onboarding). Held for 7s or until the user taps anywhere — whichever
// comes first.
//
// Mirrors the demo's splash treatment (DEMO_REFERENCE.html:5162) — navy
// gradient, brand mark, single rotating quote in display type with a small
// gold attribution. Cursor lives in localStorage so consecutive logins
// walk the full quote set before any repeats.

const HOLD_MS = 7000;
const FADE_MS = 350;

export function SplashScreen({ onDismiss }: { onDismiss: () => void }) {
  const t = useT();
  const language = useUI((s) => s.language);
  const [hiding, setHiding] = useState(false);
  const [quote] = useState(() => pickNextSplashQuote(language));

  function dismiss() {
    if (hiding) return;
    setHiding(true);
    setTimeout(onDismiss, FADE_MS);
  }

  useEffect(() => {
    const id = setTimeout(dismiss, HOLD_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="presentation"
      onClick={dismiss}
      className={`fixed inset-0 z-[9999] flex items-center justify-center cursor-pointer transition-opacity duration-300 ${
        hiding ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{
        background: 'linear-gradient(160deg, #0d1424 0%, #1a2540 50%, #0d1424 100%)',
      }}
    >
      <div className="max-w-[520px] w-full px-8 flex flex-col items-center text-center text-white animate-[splashFadeIn_.5s_ease]">
        {/* Brand mark */}
        <div className="flex items-center gap-3 mb-14">
          <div className="w-11 h-11 rounded-[11px] overflow-hidden bg-navy-800 grid place-items-center">
            <img src="/assets/busy-mascot.png" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="font-display text-[22px] tracking-[0.04em]">
            BUSY <span className="text-gold-400">BARBER</span>
          </div>
        </div>

        {/* Quote — rendered with whitespace preserved so the embedded \n
            line breaks split the quote across lines like the demo. */}
        <div className="mb-16">
          <p
            className="font-display leading-[1.25] tracking-[0.005em] text-white mb-4 whitespace-pre-line"
            style={{ fontSize: 'clamp(24px, 4.5vw, 38px)' }}
          >
            {quote.text}
          </p>
          <p className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400 m-0">
            {quote.attr}
          </p>
        </div>

        {/* Tap-to-skip hint, gentle pulse */}
        <p className="text-[11px] tracking-[0.12em] uppercase font-semibold text-white/40 m-0 animate-[splashHintPulse_1.6s_ease-in-out_infinite]">
          {t('splash.tapToContinue')}
        </p>
      </div>

      <style>{`
        @keyframes splashFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashHintPulse {
          0%, 100% { opacity: .35; }
          50%      { opacity: .7; }
        }
      `}</style>
    </div>
  );
}
